import { useRef, useState, useCallback, useLayoutEffect } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { CallbackRef } from 'app/utils/react';

export type OnResizeCallback = (node: HTMLElement) => void;

export default function useOnResize(onResize: OnResizeCallback): CallbackRef {
  const measureNodeRef = useRef<HTMLElement | null>(null);

  const lastCommittedOnResizeRef = useRef(onResize);
  // The only way to set the callback of a ResizeObserver is by passing it to
  // the constructor, which would require us to create a new ResizeObserver
  // instance. This would be wasteful, and would unnecessarily trigger the
  // callback on render, which could cause infinite loops.
  //
  // To avoid this we use the `useEffect`+`useRef` approach described here:
  // https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback
  //
  // However, in this case we need to use `useLayoutEffect` instead of
  // `useEffect` to ensure that the effect will always be run before
  // ResizeObserver triggers the callback.
  useLayoutEffect(() => {
    lastCommittedOnResizeRef.current = onResize;
  }, [onResize]);

  const [observer] = useState(() => new ResizeObserver(() => {
    if (!measureNodeRef.current) {
      // This shouldn't happen in practice, but checking just in case, and to
      // make TypeScript happy.
      return;
    }

    lastCommittedOnResizeRef.current?.(measureNodeRef.current);
  }));

  const measureRef: CallbackRef = useCallback(node => {
    if (node === null && measureNodeRef.current) {
      observer.unobserve(measureNodeRef.current);
    }

    measureNodeRef.current = node;

    if (measureNodeRef.current) {
      observer.observe(measureNodeRef.current);
    }
  }, [observer]);

  return measureRef;
}
